import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, AreaChart, Area, Legend,
} from "recharts";
import { api, formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Sparkles, FlaskConical, Trash2 } from "lucide-react";

const STATUS_TINT = {
  paid: "bg-emerald-100 text-emerald-800",
  payment_pending: "bg-amber-100 text-amber-800",
  invoice_submitted: "bg-blue-100 text-blue-800",
  completed: "bg-slate-100 text-slate-700",
  fulfillment_in_progress: "bg-indigo-100 text-indigo-700",
  vendor_accepted: "bg-violet-100 text-violet-700",
  pending_vendor_acceptance: "bg-rose-50 text-rose-700",
};

export default function PortalProgramPerformance() {
  const [data, setData] = useState(null);
  const [vfilter, setVfilter] = useState("all");
  const [cfilter, setCfilter] = useState("all");
  const [seeding, setSeeding] = useState(false);
  const [hasSample, setHasSample] = useState(false);

  const load = () => {
    api.get("/clinic/analytics/program-performance")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    api.get("/clinic/intelligence")
      .then((r) => setHasSample(!!r.data?.has_sample_data))
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/clinic/sample-data/seed");
      toast.success(`Seeded ${r.data.created} sample workflows.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSeeding(false); }
  };
  const clear = async () => {
    if (!window.confirm("Remove all sample workflows + invoices?")) return;
    try {
      await api.delete("/clinic/sample-data");
      toast.success("Sample data cleared.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filteredCategories = (data?.categories || []).filter((c) =>
    cfilter === "all" || c.category === cfilter
  );

  return (
    <PageShell
      eyebrow="Analytics · Program Performance"
      title="Monitor program growth, retention, and operational performance."
      intro="Operational visibility into cash-pay program execution. No clinical outcomes, diagnoses, or treatment-efficacy claims are displayed."
      testId="program-perf-page"
      actions={
        <div className="flex items-center gap-2">
          {hasSample ? (
            <button onClick={clear} data-testid="clear-sample" className="inline-flex items-center gap-1.5 bg-white border border-rose-200 text-rose-700 rounded-full px-4 py-2 text-sm font-semibold hover:bg-rose-50">
              <Trash2 className="w-3.5 h-3.5" /> Clear sample data
            </button>
          ) : (
            <button onClick={seed} disabled={seeding} data-testid="seed-sample" className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60">
              <FlaskConical className="w-3.5 h-3.5" /> {seeding ? "Seeding…" : "Seed sample data"}
            </button>
          )}
        </div>
      }
    >
      {hasSample && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 mb-5 text-[11px] text-amber-900 flex gap-2">
          <Sparkles className="w-3.5 h-3.5 mt-0.5 text-amber-700" />
          <div><b>Sample data active.</b> Marked with “Sample · ” names. Clear anytime to see real metrics only.</div>
        </div>
      )}

      {!data ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
            <Kpi label="Active programs" value={data.summary.active_programs} accent="bg-blue-600" />
            <Kpi label="Workflows" value={data.summary.total_workflows} accent="bg-violet-600" />
            <Kpi label="Fulfillment rate" value={`${data.summary.fulfillment_rate}%`} accent="bg-emerald-600" />
            <Kpi label="Total revenue" value={`$${data.summary.total_revenue.toLocaleString()}`} accent="bg-amber-600" />
            <Kpi label="Recurring revenue" value={`$${data.summary.recurring_revenue_estimate.toLocaleString()}`} accent="bg-indigo-600" />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            <FilterPill label="Program" value={cfilter} setValue={setCfilter} options={[["all", "All categories"], ...data.categories.map(c => [c.category, c.label])]} testId="filter-category" />
            <FilterPill label="Vendor" value={vfilter} setValue={setVfilter} options={[["all", "All vendors"]]} testId="filter-vendor" />
          </div>

          {/* Timeline */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
              Enrollment + revenue timeline
            </div>
            {data.timeline.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">No timeline data yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area yAxisId="left" type="monotone" dataKey="new" name="New workflows" stroke="#2563eb" fill="#2563eb" fillOpacity={0.15} />
                  <Area yAxisId="left" type="monotone" dataKey="completed" name="Completed" stroke="#16a34a" fill="#16a34a" fillOpacity={0.15} />
                  <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue ($)" stroke="#d97706" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Category cards + bar */}
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
                Programs by category
              </div>
              {filteredCategories.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No programs running yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={filteredCategories}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={70} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="total" name="Total" fill="#2563eb" radius={[5, 5, 0, 0]} />
                    <Bar dataKey="completed" name="Completed" fill="#16a34a" radius={[5, 5, 0, 0]} />
                    <Bar dataKey="paid" name="Paid" fill="#d97706" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-3">
                Category scorecards
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {filteredCategories.map((c) => (
                  <div key={c.category} className="rounded-xl border border-slate-100 p-3 bg-slate-50/50" data-testid={`cat-${c.category}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-slate-900 text-sm">{c.label}</div>
                      <div className="text-xs text-slate-500">${c.revenue.toLocaleString()}</div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-2 text-[11px]">
                      <Mini label="Total" v={c.total} />
                      <Mini label="In-flight" v={c.in_flight} />
                      <Mini label="Done" v={c.completed} />
                      <Mini label="Engagement" v={`${c.engagement_rate}%`} />
                    </div>
                  </div>
                ))}
                {filteredCategories.length === 0 && (
                  <div className="text-xs text-slate-500 py-3">No categories yet.</div>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 mt-5 text-[11px] text-slate-500 leading-relaxed">
            <b>Compliance:</b> Metrics here describe operational performance only — workflow throughput, fulfillment, invoice and engagement counts.
            No clinical outcomes, diagnoses, or treatment-efficacy claims are displayed or stored.
          </div>
        </>
      )}
    </PageShell>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className={`w-1.5 h-6 rounded-full ${accent} mb-2`} />
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
      <div className="font-display text-2xl font-extrabold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
function Mini({ label, v }) {
  return (
    <div className="bg-white rounded-md p-1.5 text-center border border-slate-100">
      <div className="text-[10px] text-slate-500">{label}</div>
      <div className="font-bold text-slate-900">{v}</div>
    </div>
  );
}
function FilterPill({ label, value, setValue, options, testId }) {
  return (
    <label className="inline-flex items-center gap-1 text-xs text-slate-600">
      {label}:
      <select className="ml-1 border border-slate-200 rounded-full text-xs px-2 py-1 bg-white" value={value} onChange={(e) => setValue(e.target.value)} data-testid={testId}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

void STATUS_TINT;
